import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { fmt } from '../utils';

/**
 * SOWCostTable — shows estimated / budgeted / actual for a set of cost lines.
 *
 * Props:
 *   dealId       — deal id
 *   section      — section key (e.g. 'demo', 'roof', 'room_12')
 *   lines        — [{section, label, estimated}] from computeSOWCosts
 *   sowCosts     — deal.sow_costs array from server (all sections)
 *   onSynced     — callback after sync so parent can reload deal
 */
export default function SOWCostTable({ dealId, lines, sowCosts, onSynced }) {
  const [rows, setRows] = useState([]);
  const synced = useRef(false);

  // Build display rows by merging computed lines with DB overrides
  useEffect(() => {
    if (!lines || lines.length === 0) { setRows([]); return; }

    const dbMap = {};
    for (const r of (sowCosts || [])) {
      dbMap[`${r.section}|||${r.label}`] = r;
    }

    const merged = lines.map(line => {
      const key = `${line.section}|||${line.label}`;
      const db  = dbMap[key];
      return {
        id:        db?.id ?? null,
        section:   line.section,
        label:     line.label,
        estimated: line.estimated,
        budgeted:  db?.budgeted ?? line.estimated,
        actual:    db?.actual   ?? '',
      };
    });

    setRows(merged);

    // Sync to server once per section open (so DB has fresh rows)
    if (!synced.current) {
      synced.current = true;
      api.syncSowCosts(dealId, lines.map((l, i) => ({ ...l, sort_order: i }))).then(onSynced);
    }
  }, [lines, sowCosts, dealId, onSynced]);

  const saveBudgeted = async (row, value) => {
    const v = value === '' ? row.estimated : parseFloat(value);
    setRows(r => r.map(x => x.label === row.label && x.section === row.section ? { ...x, budgeted: v } : x));
    if (row.id) await api.updateSowCostLine(dealId, row.id, { budgeted: v });
    onSynced();
  };

  const saveActual = async (row, value) => {
    const v = value === '' ? null : parseFloat(value);
    setRows(r => r.map(x => x.label === row.label && x.section === row.section ? { ...x, actual: v ?? '' } : x));
    if (row.id) await api.updateSowCostLine(dealId, row.id, { actual: v });
    onSynced();
  };

  if (rows.length === 0) return null;

  const totalBudgeted = rows.reduce((s, r) => s + (r.budgeted || 0), 0);
  const totalActual   = rows.reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);
  const hasActuals    = rows.some(r => r.actual !== '' && r.actual != null && r.actual > 0);

  return (
    <div style={{marginTop:16,borderTop:'1px solid var(--border)',paddingTop:12}}>
      <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',
        color:'var(--text-muted)',marginBottom:8}}>Cost Estimate</div>

      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead>
          <tr>
            <th style={{textAlign:'left',padding:'2px 6px 6px',color:'var(--text-muted)',fontWeight:600,fontSize:11}}>Item</th>
            <th style={{textAlign:'right',padding:'2px 6px 6px',color:'var(--text-muted)',fontWeight:600,fontSize:11}}>Est.</th>
            <th style={{textAlign:'right',padding:'2px 6px 6px',color:'var(--text-muted)',fontWeight:600,fontSize:11}}>Budgeted</th>
            <th style={{textAlign:'right',padding:'2px 6px 6px',color:'var(--text-muted)',fontWeight:600,fontSize:11}}>Actual</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{borderBottom:'1px solid var(--bg)'}}>
              <td style={{padding:'4px 6px',color:'var(--text)'}}>{row.label}</td>
              <td style={{padding:'4px 6px',textAlign:'right',color:'var(--text-muted)'}}>{fmt(row.estimated)}</td>
              <td style={{padding:'4px 6px',textAlign:'right'}}>
                <input
                  type="number"
                  defaultValue={row.budgeted}
                  onBlur={e => saveBudgeted(row, e.target.value)}
                  style={{width:80,textAlign:'right',fontSize:12,
                    background: row.budgeted !== row.estimated ? 'var(--brand-light)' : 'transparent',
                    border:'1px solid var(--border)',borderRadius:4,padding:'2px 4px'}}
                />
              </td>
              <td style={{padding:'4px 6px',textAlign:'right'}}>
                <input
                  type="number"
                  defaultValue={row.actual || ''}
                  placeholder="—"
                  onBlur={e => saveActual(row, e.target.value)}
                  style={{width:80,textAlign:'right',fontSize:12,
                    border:'1px solid var(--border)',borderRadius:4,padding:'2px 4px'}}
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'2px solid var(--border)',fontWeight:700}}>
            <td style={{padding:'6px 6px 0',fontSize:12}}>Section Total</td>
            <td />
            <td style={{padding:'6px 6px 0',textAlign:'right',color:'var(--brand)'}}>{fmt(totalBudgeted)}</td>
            <td style={{padding:'6px 6px 0',textAlign:'right',color: hasActuals ? 'var(--text)' : 'var(--text-muted)'}}>
              {hasActuals ? fmt(totalActual) : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
